import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { AsyncPipe, DOCUMENT } from '@angular/common';
import { ChangeDetectionStrategy, Component, effect, inject, OnInit } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import { MatToolbar } from '@angular/material/toolbar';
import { RouterLink } from '@angular/router';
import { MdsButtonComponent, MdsDividerComponent } from '@medpacesoftwaredevelopment/designsystem';
import { ReportBugResultViewModel } from '@models/error-details';
import { AuthService } from '@services/auth/auth.service';
import { EnvConfigurationService } from '@services/env/env-configuration.service';
import { ErrorService } from '@services/error-handling/error-service';
import { IdleTimeService } from '@services/idle-time/idle-time.service';
import { SnackbarService } from '@services/snackbar/snackbar.service';
import { getModalConfig, ModalSize } from '@utility/modal-helper';
import {
    catchError,
    distinctUntilChanged,
    filter,
    fromEvent,
    map,
    shareReplay,
    switchMap,
    tap,
    throwError,
} from 'rxjs';
import { ReportBugDialogComponent } from '../dialogs/report-bug-dialog/report-bug-dialog.component';

@Component({
    selector: 'medpace-footer',
    imports: [MdsButtonComponent, MatToolbar, AsyncPipe, RouterLink, MdsDividerComponent],
    standalone: true,
    templateUrl: './footer.component.html',
    styleUrls: ['./footer.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FooterComponent implements OnInit {
    private readonly document = inject(DOCUMENT);
    private readonly footerSize = toSignal(
        fromEvent(this.document.defaultView ?? window, 'resize').pipe(map(() => Date.now())),
        { initialValue: null }
    );
    envService = inject(EnvConfigurationService);
    idleTimeService = inject(IdleTimeService);
    breakpointObserver = inject(BreakpointObserver);
    dialog = inject(MatDialog);
    reportErrorService = inject(ErrorService);
    snackbarService = inject(SnackbarService);
    authService = inject(AuthService);

    private readonly footerHeight = effect(() => {
        const height = this.footerSize()?.[0]?.contentRect.height;
        if (height !== undefined) {
            this.document.documentElement.style.setProperty('--footer-height', `${height}px`);
        }
    });

    authenticated$ = this.authService.getIsAuthenticated().pipe(distinctUntilChanged(), shareReplay(1));

    isMobile$ = this.breakpointObserver.observe([Breakpoints.Handset, Breakpoints.TabletPortrait]).pipe(
        map((result) => result.matches),
        distinctUntilChanged()
    );

    logo = './../../../../assets/img/TrialCompanion_Color_Horizontal_RGB.svg';
    currentYear: number = new Date().getFullYear();
    appName: string = this.envService.configuration.applicationName;
    appVersion: string = this.envService.configuration.version;
    hashVersion: string = this.envService.configuration.hashVersion;
    emailAddress: string = 'PCS@medpace.com';

    timer$ = this.idleTimeService.timer$.pipe(
        map((remainingTimeInSeconds) => {
            let minutes = Math.floor(remainingTimeInSeconds / 60)
                .toString()
                .padStart(2, '0');
            let seconds = (remainingTimeInSeconds % 60).toString().padStart(2, '0');
            return `${minutes}:${seconds}`;
        })
    );

    ngOnInit(): void {
        if (!this.envService.configuration.version) {
            console.error(
                'Could not find application version in the config.json file. It might suggest that config.json contains unexpected data and is worth looking into.'
            );
        }
    }

    sendErrorsDetails() {
        const dialogRef = this.dialog.open<ReportBugDialogComponent, null, ReportBugResultViewModel>(
            ReportBugDialogComponent,
            {
                ...getModalConfig(ModalSize.Small),
            }
        );

        dialogRef
            .afterClosed()
            .pipe(
                filter(Boolean),
                filter((errorDescription) => errorDescription.save),
                switchMap((errorDescription: ReportBugResultViewModel) =>
                    this.reportErrorService.reportBug(errorDescription.description!, errorDescription.url!).pipe(
                        catchError((err) => {
                            this.snackbarService.openErrorSnackbar(
                                'Failed to send the error report. Please try again later.'
                            );
                            console.error('Failed to send error report', err);
                            return throwError(() => err);
                        })
                    )
                ),
                tap(() => {
                    this.snackbarService.openInfoSnackbar('Error report has been successfully sent.');
                    this.reportErrorService.clearErrorStateService();
                })
            )
            .subscribe();
    }
}
